import { useCallback } from 'react';

import { COMMON_TAGS, getSentryClient, UNKNOWN_TAG } from '@atlassian/search-common';

import { type PrimaryProductKey } from '../../common/constants/products';
import { useBootstrap } from '../store';

export type onError = (error: any) => Promise<void>;

export const logException = async (
	error: any,
	userId?: string | null,
	primaryProduct?: PrimaryProductKey,
): Promise<void> => {
	try {
		const SentryClient = await getSentryClient();

		if (!SentryClient) {
			return;
		}

		SentryClient.withScope((scope) => {
			for (const [key, value] of Object.entries(COMMON_TAGS)) {
				scope.setTag(key, value);
			}
			scope.setUser({ id: userId || UNKNOWN_TAG });
			scope.setTag('product', primaryProduct || UNKNOWN_TAG);
			SentryClient.captureException(error, scope);
		});
	} catch (error) {
		// When there was an error with Sentry, just output to console
		// eslint-disable-next-line no-console
		console.error(error);
	}
};

/**
 * Returns a callback that logs exceptions to Sentry. The callback returned never mutates,
 * hence it is safe to use in dependency arrays.
 */
export const useLogException = (): ((error: any) => Promise<void>) => {
	const [{ primaryProduct, user }] = useBootstrap();

	// Store data required for logging exceptions in refs to prevent re-renders
	return useCallback(
		async (error) => await logException(error, user?.id, primaryProduct),
		[primaryProduct, user?.id],
	) satisfies onError;
};
